/**
 * Die Lightbox der Produktionsbilder EINER Bestellung - Auftrag Task 4 ("Produktionsbilder-Lightbox").
 *
 * Sie zeigt die AKTUELLEN Produktionsbilder positionsuebergreifend in der Reihenfolge, die der Server
 * liefert (`backend/src/documents/repository.ts#ladePositionsdokumente`,
 * `ORDER BY oie.sort_index, dl.jtl_pos_id, dl.doc_type, updated_at, id`), navigiert per Pfeiltasten
 * und Buttons, schliesst bei Escape und gibt den Fokus an den Ausloeser zurueck.
 *
 * OHNE VORBILD IM ORIGINAL. Uebernimmt das Portal-/Dialogmuster der vorhandenen Dialoge in
 * `Positionsdokumente.tsx` (`createPortal` nach `document.body`, `board-editor-overlay open
 * positionsdokument-modal-overlay` + `board-editor-dialog overwrite-dialog`, `role="dialog"` mit
 * `aria-modal="true"`) und die Fokusfalle sinngemaess von `TimelinePage/PositionenPopup.tsx`.
 *
 * ================= Task 4 verdrahtet noch NICHTS =================
 *
 * Diese Datei wird in Task 4 von KEINER bestehenden Flaeche aufgerufen; Thumbnail-Karten und
 * Oeffnen-Ausloeser haengt Task 5 an. Die Komponente kennt darum weder Drawer noch Dokumente-Tab - sie
 * bekommt ihre Daten als Props und meldet nur `onSchliessen`.
 *
 * ================= Die Liste wird NICHT sortiert =================
 *
 * `bilder` entsteht aus GENAU EINEM `filter` ueber die bereits serverseitig sortierte Prop
 * `positionsDokumente`; der Zustand haelt nur einen INDEX in dieser Liste (`index`). Kein
 * clientseitiges `sort`, keine Sortierschluessel aus den Task-3-Feldern (`positionSortIndex`,
 * `bildSortUpdatedAt`) - die sind Anzeige-/Diagnosefelder, keine zweite Sortierquelle.
 *
 * Historienversionen und geloeschte Links sind hier nie zu sehen: sie kommen in `positionsDokumente`
 * gar nicht vor (Task 1/2). Ein eigener Filter dafuer waere genauso falsch wie einer, der sie
 * hereinholen koennte.
 *
 * ================= Die Vorschau laeuft ueber den authentifizierten Bildweg =================
 *
 * Geladen wird die LIGHTBOX-Variante (`produktionsbildPreviewPfad(..., "lightbox")`, Task 2/3), und
 * zwar ueber `useAuthentifiziertesBild` aus `components/Artikelbild.tsx` - dieselbe Begruendung wie
 * dort im Kopfkommentar: das Frontend authentifiziert ausschliesslich per `Authorization`-Header, den
 * ein `<img src="/api/...">` nicht mitschicken kann. Das ORIGINAL wird hier nie geladen, nur
 * angeboten - ueber den bestehenden Weg `ladeUndSpeichereDatei(`/api/files/:fileId`, originalName)`.
 *
 * Ein Fehlschlag (404/401/Netzfehler oder ein nicht dekodierbares Bild, `meldeAnzeigefehler`) entfernt
 * den Eintrag NICHT und veraendert keinen Index: im Dialog erscheint `Vorschau nicht verfügbar`, der
 * Download bleibt funktionsfaehig.
 *
 * ================= `positionLabel` =================
 *
 * Aus den `OrderItem`s der Bestellung über `lib/positionsAnzeige.ts`: Dokumentseite und Lightbox
 * verwenden damit dieselbe Positionskennung und denselben Fallbacktext.
 */
import { useEffect, useLayoutEffect, useRef, useState, type JSX, type RefObject } from "react";
import { createPortal } from "react-dom";
import { ladeUndSpeichereDatei } from "../../../api/basis.ts";
import { produktionsbildPreviewPfad } from "../../../api/dokumente.ts";
import type { PositionsDokument } from "../../../api/dokumente.ts";
import { useAuthentifiziertesBild } from "../../Artikelbild.tsx";
import type { Order, OrderItem } from "../../../lib/types.ts";
import { positionsbezeichnung, positionskennung } from "../../../lib/positionsAnzeige.ts";
import { meldeFehler } from "../../../state/fehlerStapel.ts";
import { Ikone } from "../../Ikone.tsx";

/** Ein Eintrag der Lightbox - die Anzeigeform EINES Produktionsbilds. */
export interface ProduktionsbildLightboxEintrag {
  linkId: string;
  jtlPosId: number;
  originalName: string;
  fileId: string;
  positionLabel: string;
}

export interface ProduktionsbildLightboxProps {
  orderId: string;
  bestellung: Order;
  positionsDokumente: readonly PositionsDokument[];
  startLinkId: string;
  ausloeserRef: RefObject<HTMLElement | null>;
  onSchliessen: () => void;
}

/**
 * Die im Dialog fokussierbaren Elemente. `button:not([disabled])` ist hier der Kern: die an den
 * Raendern deaktivierten Navigationsknoepfe sind damit NICHT Teil der Fokusfalle - Tab kann nicht auf
 * einem Knopf landen, der nichts tut.
 */
function fokussierbareElemente(dialog: HTMLElement | null): HTMLElement[] {
  return Array.from(
    dialog?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), ' +
        'select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ) || [],
  );
}

export function ProduktionsbildLightbox({
  orderId,
  bestellung,
  positionsDokumente,
  startLinkId,
  ausloeserRef,
  onSchliessen,
}: ProduktionsbildLightboxProps): JSX.Element {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const schliessenRef = useRef<HTMLButtonElement | null>(null);

  // EINMALIG beim ersten Rendern aus der Prop gebildet (kein `useMemo`): der Startwert ist der
  // uebergebene Link, ein spaeterer Wechsel der Prop ist kein Oeffnungsvorgang mehr - die Komponente
  // wird von Task 5 je Oeffnung neu gemountet. Faellt der Link aus der Liste (z. B. zwischenzeitlich
  // geloescht), bleibt es beim ersten Bild statt bei einem Fehlerzustand.
  const [bilder] = useState<readonly ProduktionsbildLightboxEintrag[]>(() => {
    const positionen = (Array.isArray(bestellung.items) ? bestellung.items : []) as OrderItem[];
    return positionsDokumente
      .filter((dokument) => dokument.docType === "produktionsbilder")
      .map((dokument) => {
        const index = positionen.findIndex((position) => positionskennung(position) === dokument.jtlPosId);
        return {
          linkId: dokument.linkId,
          jtlPosId: dokument.jtlPosId,
          originalName: dokument.originalName,
          fileId: dokument.fileId,
          positionLabel: index >= 0 ? positionsbezeichnung(positionen[index], index) : "Ohne Artikelbezeichnung",
        };
      });
  });

  const [index, setIndex] = useState(() => {
    const start = bilder.findIndex((bild) => bild.linkId === startLinkId);
    return start >= 0 ? start : 0;
  });

  const aktuell = bilder[index];
  const anzahl = bilder.length;
  const vorherigesMoeglich = index > 0;
  const naechstesMoeglich = index < anzahl - 1;

  // Der Zustand haelt nur den Index - die Vorschau-URL wird bei jedem Rendern aus den aktuellen Daten
  // gebildet. `pfad` ist `null`, wenn es kein Bild gibt; `aktiv` ist hier fest `true` (der Dialog ist
  // beim Rendern bereits sichtbar), `hatBild` bleibt `undefined`: die Vorschau ist ein anderer Abruf
  // als das Artikelbild und hat keine Vorab-Auskunft.
  const pfad = aktuell
    ? produktionsbildPreviewPfad(orderId, aktuell.jtlPosId, aktuell.linkId, "lightbox", aktuell.fileId)
    : null;
  const { objectUrl, fehlgeschlagen, meldeAnzeigefehler } = useAuthentifiziertesBild(pfad, true, undefined);

  function schliessen(): void {
    onSchliessen();
    ausloeserRef.current?.focus();
  }

  // Fokus beim Oeffnen auf den Schliessen-Knopf - wie `Positionsdokumente.tsx` (dortiger
  // `useLayoutEffect` auf den Schliessen-Knopf des jeweils offenen Dialogs).
  useLayoutEffect(() => {
    schliessenRef.current?.focus();
  }, []);

  useEffect(() => {
    const beiTaste = (ereignis: KeyboardEvent): void => {
      if (ereignis.key === "Escape") {
        ereignis.preventDefault();
        schliessen();
        return;
      }
      if (ereignis.key === "ArrowLeft") {
        ereignis.preventDefault();
        setIndex((wert) => (wert > 0 ? wert - 1 : wert));
        return;
      }
      if (ereignis.key === "ArrowRight") {
        ereignis.preventDefault();
        setIndex((wert) => (wert < bilder.length - 1 ? wert + 1 : wert));
        return;
      }
      if (ereignis.key !== "Tab") return;
      // Echte Fokusfalle (mehrere fokussierbare Elemente) - wie `PositionenPopup.tsx`: Tab laeuft
      // innerhalb der Ringliste weiter und springt nur an den ENDEN um. Die deaktivierten
      // Randknoepfe stehen ueber `fokussierbareElemente` gar nicht erst in der Liste.
      const elemente = fokussierbareElemente(dialogRef.current);
      if (!elemente.length) return;
      const erstes = elemente[0];
      const letztes = elemente[elemente.length - 1];
      if (ereignis.shiftKey && document.activeElement === erstes) {
        ereignis.preventDefault();
        letztes.focus();
      } else if (!ereignis.shiftKey && document.activeElement === letztes) {
        ereignis.preventDefault();
        erstes.focus();
      }
    };
    document.addEventListener("keydown", beiTaste);
    return () => document.removeEventListener("keydown", beiTaste);
  });

  // Leere Liste (keine Produktionsbilder, oder ein `startLinkId`, der zu keinem passt, weil die Liste
  // zwischenzeitlich neu geladen wurde): die Komponente zeigt dann NICHTS - kein leerer Dialog mit
  // irrefuehrendem `1 / 0`-Zaehler. Der leere Portal-Aufruf haelt nur den Rueckgabetyp `JSX.Element`
  // der vorgegebenen Schnittstelle ein; Task 5 oeffnet die Lightbox ohnehin nur zu einem vorhandenen
  // Thumbnail.
  if (!aktuell) return createPortal(<></>, document.body);

  // Ein gescheiterter Download war hier eine unbehandelte Ablehnung (Spec R6, Fehleranzeige Task 5):
  // jetzt meldet er an den Stapel, der ueber dem modalen Dialog liegt (kein eigener Fehlerplatz im Dialog).
  const originalHerunterladen = async (): Promise<void> => {
    try {
      await ladeUndSpeichereDatei(`/api/files/${encodeURIComponent(aktuell.fileId)}`, aktuell.originalName);
    } catch (ursache) {
      meldeFehler(ursache, "Das Original konnte nicht heruntergeladen werden.");
    }
  };

  return createPortal(
    <div className="board-editor-overlay open positionsdokument-modal-overlay" role="presentation">
      <section
        ref={dialogRef}
        className="board-editor-dialog overwrite-dialog produktionsbild-lightbox"
        role="dialog"
        aria-modal="true"
        aria-label="Produktionsbilder-Lightbox"
      >
        <div className="board-editor-head">
          <h2>Produktionsbilder</h2>
          <button
            ref={schliessenRef}
            className="icon-btn"
            type="button"
            aria-label="Schließen"
            onClick={schliessen}
          >
            <Ikone name="x" />
          </button>
        </div>
        <div className="board-editor-body produktionsbild-lightbox-koerper">
          <p className="produktionsbild-lightbox-position">{aktuell.positionLabel}</p>
          {objectUrl ? (
            <img
              className="produktionsbild-lightbox-bild"
              src={objectUrl}
              alt={`${aktuell.originalName} Vorschau`}
              onError={meldeAnzeigefehler}
            />
          ) : (
            // Solange geladen wird, steht hier nichts Sichtbares - der Fehlschlag ist ein EIGENER
            // Zustand (siehe `fehlgeschlagen`), kein Ladezustand.
            fehlgeschlagen
              ? <p className="produktionsbild-lightbox-hinweis">Vorschau nicht verfügbar</p>
              : null
          )}
          <div className="produktionsbild-lightbox-meta">
            <strong>{aktuell.originalName}</strong>
            <span>{`${index + 1} / ${anzahl}`}</span>
          </div>
        </div>
        <div className="board-editor-foot produktionsbild-lightbox-navigation">
          <button
            className="btn-sec"
            type="button"
            aria-label="Vorheriges Bild"
            disabled={!vorherigesMoeglich}
            onClick={() => setIndex((wert) => (wert > 0 ? wert - 1 : wert))}
          >
            Zurück
          </button>
          <button
            className="btn-sec"
            type="button"
            aria-label="Nächstes Bild"
            disabled={!naechstesMoeglich}
            onClick={() => setIndex((wert) => (wert < anzahl - 1 ? wert + 1 : wert))}
          >
            Weiter
          </button>
          <button
            className="btn-primary"
            type="button"
            aria-label="Original herunterladen"
            onClick={() => void originalHerunterladen()}
          >
            Original herunterladen
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}
